import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { drawerDurationMs } from '../utils/cssDuration';
import { isMobileViewport } from '../utils/breakpoints';

/* Swipe tuning */
const DIR_LOCK = 10; // px before the gesture picks an axis
const COMMIT_SHARE = 0.3; // dragged this share of the panel width → close
const COMMIT_VELOCITY = 0.3; // px/ms — a flick closes from anywhere

const PUSH_CLASS = 'ds-side-menu-push';
const PUSH_OPEN_CLASS = 'ds-side-menu-push--open';
/* On <html>: clips the pushed page's overflow (SideMenu.css) */
const PUSHING_CLASS = 'ds-side-menu-pushing';

/**
 * Phones (< 1024px, Figma OVER-LL 2778:29896): the SideMenu pushes the page
 * aside and closes with a swipe to the left.
 *
 * Push — the element matched by `pushTarget` (the app root) gets
 * `.ds-side-menu-push` (+ `--open`), which moves it right by the panel width
 * with `position: relative; left` (SideMenu.css). NOT a transform: a transform
 * on the page's ancestor would re-anchor every fixed element inside it (the
 * page's sticky bars would jump off-screen). The CSS lives under the mobile
 * media query, so a viewport that grows to desktop drops the push.
 *
 * Swipe — the panel, the overlay and the page follow the finger (inline
 * styles, no re-render); a drag past 30% of the panel or a flick commits,
 * anything else snaps back.
 *
 * Returns the touch handlers (panel + overlay) and `clearDrag()`, which the
 * menu calls before its own close animation so a snapped-back drag's inline
 * `animation: none` doesn't swallow it.
 */
export default function useSideMenuPush({ active, closing, panelRef, overlayRef, pushTarget, onSwipeClose }) {
  const targetRef = useRef(null);
  const widthRef = useRef(0);
  const touchRef = useRef(null);
  const timerRef = useRef(null);

  /* ── Push the page while the menu is shown (phones only) ── */
  useLayoutEffect(() => {
    if (!active || !pushTarget || !isMobileViewport()) return undefined;
    const target = document.querySelector(pushTarget);
    const panel = panelRef.current;
    if (!target || !panel) return undefined;

    targetRef.current = target;
    const measure = () => {
      widthRef.current = panel.offsetWidth;
      target.style.setProperty('--side-menu-push', `${widthRef.current}px`);
    };
    measure();
    document.documentElement.classList.add(PUSHING_CLASS);
    target.classList.add(PUSH_CLASS);
    // Commit `left: 0` first, so the push transitions instead of jumping
    void target.offsetWidth; // eslint-disable-line no-void
    target.classList.add(PUSH_OPEN_CLASS);
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);
      target.classList.remove(PUSH_CLASS, PUSH_OPEN_CLASS);
      document.documentElement.classList.remove(PUSHING_CLASS);
      target.style.removeProperty('--side-menu-push');
      target.style.removeProperty('left');
      target.style.removeProperty('transition');
      targetRef.current = null;
    };
  }, [active, pushTarget, panelRef]);

  /* ── The menu's own close animation: the page slides back with it ── */
  useEffect(() => {
    if (closing) targetRef.current?.classList.remove(PUSH_OPEN_CLASS);
  }, [closing]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  /* ── Drag styles ── */
  const paint = useCallback((offset, animate) => {
    const panel = panelRef.current;
    const overlay = overlayRef.current;
    const target = targetRef.current;
    const width = widthRef.current || panel?.offsetWidth || 1;
    const handle = panel?.querySelector('.ds-side-menu__handle');
    const progress = Math.max(0, 1 + offset / width); // 1 open → 0 closed
    const motion = animate
      ? `var(--transition-time-drawer) var(--transition-function-drawer)`
      : null;
    // Fades run on the even UI curve (the drawer curve is front-loaded); the
    // handle is gone by half time, before it would reach the screen edge
    const fade = animate ? 'var(--transition-time-drawer) var(--transition-function-ui)' : null;
    const handleFade = animate ? 'calc(var(--transition-time-drawer) / 2) var(--transition-function-ui)' : null;

    if (panel) {
      panel.style.animation = 'none';
      panel.style.transition = motion ? `transform ${motion}` : 'none';
      panel.style.transform = `translateX(${offset}px)`;
    }
    if (overlay) {
      overlay.style.animation = 'none';
      overlay.style.transition = fade ? `opacity ${fade}` : 'none';
      overlay.style.opacity = String(progress);
    }
    if (handle) {
      handle.style.animation = 'none';
      handle.style.transition = handleFade ? `opacity ${handleFade}` : 'none';
      // Leaves twice as fast as the panel, so it never rests on the screen edge
      handle.style.opacity = String(Math.max(0, 2 * progress - 1));
    }
    if (target) {
      target.style.transition = motion ? `left ${motion}` : 'none';
      target.style.left = `${Math.max(0, width + offset)}px`;
    }
  }, [panelRef, overlayRef]);

  const clearDrag = useCallback(() => {
    clearTimeout(timerRef.current);
    const handle = panelRef.current?.querySelector('.ds-side-menu__handle');
    [panelRef.current, overlayRef.current, handle].forEach((el) => {
      if (!el) return;
      ['animation', 'transition', 'transform', 'opacity'].forEach((p) => el.style.removeProperty(p));
    });
    const target = targetRef.current;
    if (target) {
      target.style.removeProperty('left');
      target.style.removeProperty('transition');
    }
  }, [panelRef, overlayRef]);

  /* ── Touch handlers ── */
  const onTouchStart = useCallback((e) => {
    if (!active || closing || !isMobileViewport()) return;
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, dir: null, offset: 0, lastX: t.clientX, lastT: e.timeStamp, v: 0 };
  }, [active, closing]);

  const onTouchMove = useCallback((e) => {
    const s = touchRef.current;
    if (!s) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (!s.dir) {
      if (Math.abs(dx) < DIR_LOCK && Math.abs(dy) < DIR_LOCK) return;
      s.dir = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (s.dir !== 'h') return;

    const dt = e.timeStamp - s.lastT;
    if (dt > 0) s.v = (t.clientX - s.lastX) / dt;
    s.lastX = t.clientX;
    s.lastT = e.timeStamp;
    s.offset = Math.min(0, dx);
    paint(s.offset, false);
  }, [paint]);

  const onTouchEnd = useCallback(() => {
    const s = touchRef.current;
    touchRef.current = null;
    if (!s || s.dir !== 'h') return;

    const width = widthRef.current || panelRef.current?.offsetWidth || 1;
    const commit = -s.offset > width * COMMIT_SHARE || (s.v < -COMMIT_VELOCITY && s.offset < 0);
    paint(commit ? -width : 0, true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (commit) onSwipeClose();
      else {
        // Back in place: hand `left` back to the --open class (same value)
        const target = targetRef.current;
        target?.style.removeProperty('left');
        target?.style.removeProperty('transition');
      }
    }, drawerDurationMs());
  }, [paint, panelRef, onSwipeClose]);

  return { swipeHandlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd }, clearDrag };
}
